import React, { useCallback, useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { NavLink, useSearchParams } from "react-router-dom";

import { useFlows } from "src/api/queries";
import { Icon } from "src/design-system/Icon";
import { useEntitySchemas } from "src/entities/queries";
import { FeatureCatalogueWrapper } from "src/featureCatalogue/FeatureCatalogue";
import { FeatureQueryMetrics } from "src/featureCatalogue/Features/FeatureQueryMetrics";
import { Features } from "src/featureCatalogue/Features/Features";
import {
  ViewByType,
  ViewTypeDropdown,
} from "src/featureCatalogue/Features/ViewTypeDropdown";
import { Queries } from "src/featureCatalogue/Queries/Queries";
import { useStatusToggle } from "src/featureCatalogue/useStatusToggle";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { useAllJobs } from "src/jobs/api/queries";
import { SubHeaderSearchBox } from "src/layout/SubHeader";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { getUrlToManageFeatureQueryPage } from "src/router/urls";

const FeaturesHeader: React.FC<{
  onSearch: (query: string) => void;
  currentViewOption: ViewByType;
  onCurrentViewChange: (view: ViewByType) => void;
  searchKey: number;
}> = ({ onSearch, currentViewOption, onCurrentViewChange, searchKey }) => {
  const { featureQueriesCatalogue } = useCapabilities();
  const { orgId, workspace } = useWorkspaceContext();

  // Prefetch data used so it's available instantly during the confirmation modal
  useFlows({ includeSchemas: true, workspaceId: workspace.id });
  useAllJobs(workspace.base_url!, { statuses: ["active"] });
  useEntitySchemas({ baseUrl: workspace.base_url! });

  return (
    <>
      <ViewTypeDropdown
        selected={currentViewOption}
        onSelect={onCurrentViewChange}
      />
      <SubHeaderSearchBox
        key={searchKey}
        dataLoc="features-search"
        defaultValue=""
        placeholder="Search..."
        onChange={onSearch}
      />
      {featureQueriesCatalogue.canCreateQuery && (
        <div className="flex items-center border-l p-3.5">
          <NavLink
            data-loc="features-manage-link"
            to={getUrlToManageFeatureQueryPage(orgId, workspace.id)}
          >
            <Icon icon={faPlus} />
          </NavLink>
        </div>
      )}
    </>
  );
};

export const FeaturesPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchKey, setSearchKey] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentViewOption =
    (searchParams.get("view") as ViewByType) ?? "Feature";
  const setCurrentViewOption = useCallback(
    (view: ViewByType) => {
      setSearchParams((prev) => {
        prev.set("view", view);
        return prev;
      });
    },
    [setSearchParams],
  );

  const resetSearch = useCallback(() => {
    setSearchQuery("");
    setSearchKey((k) => k + 1);
  }, []);

  useDocumentTitle(["Features"]);

  const { handleRequestStatusChange, statusToggleModal } = useStatusToggle();

  return (
    <FeatureCatalogueWrapper
      subHeaderRightContent={
        <FeaturesHeader
          currentViewOption={currentViewOption}
          searchKey={searchKey}
          onCurrentViewChange={setCurrentViewOption}
          onSearch={setSearchQuery}
        />
      }
    >
      <FeatureQueryMetrics />
      <div
        className="flex min-h-0 w-full flex-1 flex-col"
        data-loc="features-page-content"
      >
        {currentViewOption === "Query" ? (
          <Queries
            resetSearch={resetSearch}
            searchQuery={searchQuery}
            onRequestStatusChange={handleRequestStatusChange}
          />
        ) : (
          <Features
            resetSearch={resetSearch}
            searchQuery={searchQuery}
            onRequestStatusChange={handleRequestStatusChange}
          />
        )}
      </div>
      {statusToggleModal}
    </FeatureCatalogueWrapper>
  );
};
